@if (!isEditing) {
    <adapt-universal-dashboard-element name="Team roles"
                                       [nested]="nested"
                                       [background]="!nested"
                                       [disableClick]="nested || !canEdit"
                                       (headerClicked)="edit().subscribe()">
        @if (team) {
            <ng-container adapt-header-action-right
                          *ngTemplateOutlet="controls"></ng-container>
        }

        <ng-container *ngTemplateOutlet="content"></ng-container>
    </adapt-universal-dashboard-element>
} @else {
    <ng-container *ngTemplateOutlet="content"></ng-container>
}

<ng-template #controls>
    <div class="d-flex flex-row gap-3 flex-wrap">
        @if (isEditing) {
            <adapt-team-roles-new-role-button [team]="team"
                                              (roleAdded)="onRolesAdded([$event])"
                                              data-test="team-roles-new-role-button"></adapt-team-roles-new-role-button>

            <adapt-team-roles-pin-existing-role-button [team]="team"
                                                       (roleLocationsAdded)="onRolesAdded($event)"
                                                       data-test="team-roles-pin-existing-role-button"></adapt-team-roles-pin-existing-role-button>
        }

        @if (!isEditing && visibleRoles.length > 0) {
            <button class="ms-auto"
                    adaptButtonIcon="fal fa-users"
                    adaptTooltip="Group by person"
                    [hideTooltipOnBlurred]="false"
                    [adaptButtonToggle]="groupByPerson"
                    (adaptButtonToggleChange)="groupByPerson = !groupByPerson"></button>

            <button adaptTooltip="{{hidePeople ? 'Show' : 'Hide'}} team members"
                    [hideTooltipOnBlurred]="false"
                    [(adaptCollapsibleButton)]="hidePeople"></button>
        }

        @if (!isEditing && nested && canEdit) {
            <button class="btn btn-pill"
                    adaptButton="edit"
                    data-test="edit-mode-button"
                    [adaptBlockingClick]="edit"></button>
        }
    </div>
</ng-template>

<ng-template #content>
    @if (isEditing) {
        <div class="people-header position-sticky top-0 z-3 pt-1 pb-2 mx-n3 px-3 d-flex flex-column gap-2 mb-2"
             [class.bg-white]="nested"
             data-test="team-roles-people-header">
            <h3 class="mb-0">People in team</h3>
            <div [nxtSortablejs]="undefined"
                 [config]="peopleListOptions"
                 class="d-flex flex-row flex-wrap gap-2 person-list"
                 [class.is-editing]="isEditing">
                @for (connection of activeConnections | keyvalue: null; track connection.key) {
                    <div class="card card-person m-0"
                         data-test="team-roles-person-card"
                         [attr.data-connection-id]="connection.key">
                        <div class="d-flex card-body px-1">
                            <adapt-link-person [person]="connection.value.person"
                                               [showImage]="true"
                                               [linkDisabled]="true"
                                               imageSize="sm"></adapt-link-person>
                        </div>
                    </div>
                }
            </div>
        </div>
    }

    <div class="d-flex flex-column gap-3">
        @if (isEditing) {
            <ng-container *ngTemplateOutlet="controls"></ng-container>
        }

        <div class="list"
             data-test="team-roles-list">
            <div class="row g-2">
                <div class="col role-grid">
                    @if (roles.length === 0 || visibleRoles.length === 0) {
                        <div class="text-muted">
                            @if (roles.length > 0) {
                                There are no roles pinned to the team.
                            } @else {
                                There are no roles currently defined.
                            }
                        </div>
                    } @else if (groupByPerson) {
                        @for (connection of activeConnections | keyvalue: null; track connection.key) {
                            <div class="card card-role m-0 is-drop-allowed">
                                <div class="card-header px-2">
                                    <adapt-link-person [person]="connection.value.person"
                                                       [showImage]="true"
                                                       [linkDisabled]="false"
                                                       [showPopover]="false"
                                                       imageSize="sm"></adapt-link-person>
                                </div>
                                <div [collapse]="hidePeople"
                                     [isAnimated]="true">
                                    <div class="card-body px-1 py-2 card-role-list d-flex flex-column gap-2">
                                        @if (rolesForActiveConnections.get(connection.value); as roleConnections) {
                                            @for (role of roleConnections; track role.roleId) {
                                                <adapt-link-role [role]="role"
                                                                 [showIcon]="true"></adapt-link-role>
                                            } @empty {
                                                <p class="text-muted px-1">No pinned roles assigned to this team member yet.</p>
                                            }
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    } @else {
                        @for (role of visibleRoles; track role.roleId) {
                            @if (activeConnectionsForRole.get(role); as roleConnections) {
                                @let isDropAllowed = !personDragging || (!roleConnections.has(personDragging) && !role.extensions.isSystemAllocatedRole());

                                <div class="card card-role m-0"
                                     [attr.data-role-id]="role.roleId"
                                     [class.is-editing]="isEditing"
                                     [class.person-dragging]="personDragging"
                                     [class.is-drop-allowed]="isDropAllowed">
                                    <div class="card-header px-1">
                                        <adapt-link-role [role]="role"
                                                         [showIcon]="true"
                                                         [openConfigOnClick]="isEditing"
                                                         (roleConfigured)="updateData()"></adapt-link-role>
                                        @if (isEditing) {
                                            <button adaptButton="pinBorderless"
                                                    adaptTooltip="Unpin role from team"
                                                    [adaptBlockingClick]="togglePinnedRole"
                                                    [adaptBlockingClickParam]="role"
                                                    class="ms-auto"></button>
                                        }
                                    </div>
                                    <div [collapse]="hidePeople"
                                         [isAnimated]="true">
                                        <div class="card-body p-2 card-role-list d-flex flex-column gap-2">
                                            @if ((!isEditing || role.extensions.isSystemAllocatedRole()) && roleConnections.size === 0) {
                                                <p class="text-muted">No team members assigned to this role yet.</p>
                                            }

                                            @if (roleConnections.size > 0) {
                                                <!-- show all people when editing -->
                                                <adapt-see-more [maxHeight]="isEditing ? 9999 : 130"
                                                                gradient>
                                                    <div class="card-role-people-list"
                                                         [class.is-editing]="isEditing">
                                                    @for (connection of roleConnections; track connection.connectionId) {
                                                            <adapt-team-roles-connection [isEditing]="isEditing"
                                                                                         [role]="role"
                                                                                         [connection]="connection"
                                                                                         (roleConnectionChanged)="updateData()"
                                                                                         data-test="team-role-connection"></adapt-team-roles-connection>
                                                        }
                                                    </div>
                                                </adapt-see-more>
                                            }

                                            @if (isEditing && !role.extensions.isSystemAllocatedRole()) {
                                                <div [nxtSortablejs]="undefined"
                                                     [config]="roleListOptions"
                                                     [attr.data-role-id]="role.roleId"
                                                     class="card-role-sortable d-flex flex-grow-1"
                                                     [class.is-drop-allowed]="isDropAllowed"></div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }
                        }

                        @if (visibleRoles.length === 1 && this.isEditing) {
                            <!-- placeholder so that the grid doesn't look dumb when there's only 1 role -->
                            <div class="grid-placeholder"></div>
                        }
                    }
                </div>
            </div>
        </div>
    </div>
</ng-template>
